import { ICellValue, ITableViewColumn, ITableViewRowLock } from 'types'
import { SELECT_COLUMN_TYPES, TEXT_COLUMN_TYPES, ViewTypes } from 'components/spreadsheet/constants/const'
import { IColumnValuesCount, IViewPortColumn } from '../types'
import { isColumnLockedByRules, resolveDependencyCachedOptions } from 'components/spreadsheet/helpers/functions'
import { getDuplicateValueKey, isCaseInsensitiveDuplicateKind } from 'components/spreadsheet/helpers/duplicates'

// ─── Memoized options Set ───────────────────────────────────────────────────
// Both the per-cell validation effect (cell/index.tsx) and the aggregate
// footer (aggregate.ts) need to check whether a select-column cell value is
// one of the column's allowed options. The naive implementation —
// `[...cachedOptions, ...manualOptions].find(o => o.toLocaleString() === value.toLocaleString())` —
// allocates a new array of up to a thousand entries per call and does a
// linear scan with two string allocations per comparison. With virtualized
// grids that re-validate ~30 visible cells per render plus an aggregate
// recompute, this becomes the dominant cost on reference-table-backed
// columns.
//
// Cache key is the column object: WeakMap keys on identity, so when the
// column is replaced (e.g. after a column edit) the entry is garbage
// collected. We additionally invalidate by reference equality of cached /
// manual options so REFRESH_SELECT_OPTIONS or any reducer that produces a
// fresh array forces a rebuild on next read.
//
// The cache only handles the flat `string[]` form of cachedOptions. For
// dependency-bearing columns (`cachedOptions` is `{ [depKey]: string[] }`),
// the resolved options vary per row, so callers should resolve via
// `resolveDependencyCachedOptions` and then call `buildOptionsSet` directly.
interface OptionsSetCacheEntry {
  cachedOptionsRef: unknown
  manualOptionsRef: unknown
  set: Set<string>
}

const flatOptionsSetCache = new WeakMap<ITableViewColumn, OptionsSetCacheEntry>()
const EMPTY_OPTIONS: readonly string[] = Object.freeze([])

export const buildOptionsSet = (cached: readonly unknown[], manual: readonly unknown[]): Set<string> => {
  const set = new Set<string>()
  for (let i = 0; i < cached.length; i++) {
    const o = cached[i]
    if (o) set.add(typeof o === 'string' ? o : (o as { toLocaleString(): string }).toLocaleString())
  }
  for (let i = 0; i < manual.length; i++) {
    const o = manual[i]
    if (o) set.add(typeof o === 'string' ? o : (o as { toLocaleString(): string }).toLocaleString())
  }
  return set
}

export const getOptionsSetForColumn = (
  column: ITableViewColumn,
  cachedOptions: readonly unknown[] | undefined,
  manualOptions: readonly unknown[] | undefined
): Set<string> => {
  const cached = cachedOptions ?? EMPTY_OPTIONS
  const manual = manualOptions ?? EMPTY_OPTIONS
  const entry = flatOptionsSetCache.get(column)
  if (entry && entry.cachedOptionsRef === cached && entry.manualOptionsRef === manual) {
    return entry.set
  }
  const set = buildOptionsSet(cached, manual)
  flatOptionsSetCache.set(column, { cachedOptionsRef: cached, manualOptionsRef: manual, set })
  return set
}

export const isColumnReadOnly = (
  column: ITableViewColumn,
  isContributor: boolean,
  rowLockSettings: ITableViewRowLock[]
): boolean => {
  return (
    isColumnLockedByRules(rowLockSettings, column.publicId) ||
    !isContributor ||
    column.scriptEnabled ||
    column.formulaEnabled ||
    column.isJoined
  )
}

export const isReadOnly = (column: IViewPortColumn, isContributor: boolean, rowLockSettings: ITableViewRowLock[]) =>
  isColumnReadOnly(column.column, isContributor, rowLockSettings)

export const isValidString = (
  value: string,
  stringValidation: string,
  validationMessage: string,
  hardValidation: boolean
) => {
  let regexMatch = false
  try {
    const regex = new RegExp(stringValidation)
    regexMatch = regex.test(value)
  } catch {
    /* no-op */
  }
  return { error: !regexMatch, warn: !hardValidation, errorMessage: !regexMatch ? validationMessage : '' }
}

export const isValidSelect = (value: string, allOptions: string[]) => {
  const valid = allOptions.find((option) => option && value && option.toLocaleString() === value.toLocaleString())
  const newValidationMessage = `Warning: That select option is no longer valid`
  return { error: !valid, warn: true, errorMessage: !valid ? newValidationMessage : '' }
}

export const isValidMultiSelect = (value: string[], allOptions: string[]) => {
  const valid = (value as string[])
    .map((currentVal) =>
      allOptions.find((option) => currentVal && option && option.toLocaleString() === currentVal.toLocaleString())
    )
    .every(Boolean)
  const newValidationMessage = `Warning: One of the select options is no longer valid`
  return { error: !valid, warn: true, errorMessage: !valid ? newValidationMessage : '' }
}

type ValidationFailure = 'blank' | 'duplicate' | 'format' | 'select'

const defaultValidationMessage = (failure: ValidationFailure, columnName: string, value: ICellValue): string => {
  switch (failure) {
    case 'blank':
      return `Column '${columnName}' can't be left blank. Enter a value.`
    case 'duplicate':
      return `'${value}' is already used in another row. Each value in column '${columnName}' must be unique.`
    case 'format':
      return `'${value}' doesn't match the format set for column '${columnName}'.`
    case 'select':
      return Array.isArray(value)
        ? `Some of these options aren't in the list for column '${columnName}'. Choose options from the list.`
        : `'${value}' isn't one of the options for column '${columnName}'. Choose an option from the list.`
  }
}

export const isValidValue = (
  value: ICellValue,
  rowData:
    | {
        [columnId: string]: ICellValue
      }
    | null
    | undefined,
  seenColumnValues: IColumnValuesCount,
  column: ITableViewColumn,
  formValues?: Record<string, ICellValue>,
  type?: number,
  future?: boolean
): {
  error: boolean
  warn: boolean
  errorMessage: string
} => {
  const kind = column.kind

  let blankErrorTriggered = false
  let duplicateErrorTriggered = false
  let stringErrorTriggered = false
  let selectErrorTriggered = false

  const validation = {
    error: false,
    warn: false,
    errorMessage: ''
  }

  if (
    column.validationNoBlanks &&
    (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))
  ) {
    blankErrorTriggered = true
  }

  // JSON columns are excluded from duplicate validation — every JSON object would
  // collapse to '[object Object]' via toString(), producing false positives. The
  // column editor should also hide validationNoDuplicates for json columns; this
  // guard is the runtime backstop.
  if (kind !== 'json' && column.validationNoDuplicates && value !== undefined && value !== null) {
    // Email columns tally case-insensitively, every other kind exact-match.
    const valueKey = getDuplicateValueKey(value.toString(), isCaseInsensitiveDuplicateKind(kind))
    if (
      seenColumnValues[column.publicId] &&
      seenColumnValues[column.publicId][valueKey] !== undefined &&
      seenColumnValues[column.publicId][valueKey] + (future ? 1 : 0) > 1
    ) {
      duplicateErrorTriggered = true
    }
  }

  if (TEXT_COLUMN_TYPES.includes(kind) && column.stringValidation && column.stringValidation !== '') {
    // Blanks are governed by validationNoBlanks (handled above), not the regex.
    if (value === null || value === undefined || value === '') {
      // Skip — the blank check is the sole authority on blank rejection.
    } else if (typeof value === 'string') {
      const newValidation = isValidString(value, column.stringValidation, '', column.hardValidation)
      if (newValidation.error) {
        stringErrorTriggered = true
      }
    } else {
      stringErrorTriggered = true
    }
  }

  if (SELECT_COLUMN_TYPES.includes(kind) && !column.isJoined) {
    const kindOptions = column.kindOptions
    const tableOptions = kindOptions?.tableOptions
    const manualOptions = kindOptions?.manualOptions
    const hasDependencies = !!tableOptions?.dependencies?.length

    let optionsSet: Set<string>
    if (hasDependencies) {
      // Dependency-bearing columns resolve options per-row from the
      // dependency-keyed dict, so the result varies and can't be cached on the
      // column. Build a fresh Set per call — still O(1) lookup wins over the
      // old linear .find() with .toLocaleString() per option.
      const cachedOptionsDict = (tableOptions?.cachedOptions as { [key: string]: string[] }) || {}
      const resolved = resolveDependencyCachedOptions(cachedOptionsDict, tableOptions.dependencies, (columnId) => {
        if (type === ViewTypes.FORM) return formValues ? formValues[columnId] : undefined
        return rowData ? rowData[columnId] : undefined
      })
      optionsSet = buildOptionsSet(resolved, manualOptions ?? EMPTY_OPTIONS)
    } else {
      const cachedOptions = tableOptions?.cachedOptions as string[] | undefined
      optionsSet = getOptionsSetForColumn(column, cachedOptions, manualOptions)
    }

    if (kind === 'select' && value && typeof value === 'string') {
      if (!optionsSet.has(value)) {
        selectErrorTriggered = true
      }
    } else if (kind === 'multiselect' && value && Array.isArray(value)) {
      let allValid = true
      for (let i = 0; i < value.length; i++) {
        const v = value[i]
        if (!v) {
          allValid = false
          break
        }
        const key = typeof v === 'string' ? v : (v as { toLocaleString(): string }).toLocaleString()
        if (!optionsSet.has(key)) {
          allValid = false
          break
        }
      }
      if (!allValid) selectErrorTriggered = true
    }
  }

  // Work out whether to set the error
  validation.error = blankErrorTriggered || duplicateErrorTriggered || stringErrorTriggered || selectErrorTriggered

  // Decide whether the error should just be a warning or block submission
  const blankErrorBlock = blankErrorTriggered && column.hardValidation
  const duplicateErrorBlock = duplicateErrorTriggered && column.hardValidation
  const stringErrorBlock = stringErrorTriggered && column.hardValidation
  const selectErrorBlock = selectErrorTriggered && column.blockInvalidSelectValues

  const blockError = blankErrorBlock || duplicateErrorBlock || stringErrorBlock || selectErrorBlock
  validation.warn = validation.error && !blockError

  if (validation.error) {
    // A column's own validation message wins; otherwise explain the first rule that blocks the value
    // (or, for a warning, the first rule it breaks). Blocking errors open the error modal, which
    // already says "Error", so only warnings carry a prefix.
    const failures: [boolean, boolean, ValidationFailure][] = [
      [blankErrorTriggered, blankErrorBlock, 'blank'],
      [duplicateErrorTriggered, duplicateErrorBlock, 'duplicate'],
      [stringErrorTriggered, stringErrorBlock, 'format'],
      [selectErrorTriggered, selectErrorBlock, 'select']
    ]
    const failure = (failures.find(([triggered, blocks]) => triggered && blocks) ??
      failures.find(([triggered]) => triggered))![2]
    const message = column.validationMessage || defaultValidationMessage(failure, column.name, value)
    validation.errorMessage = blockError ? message : `Warning: ${message}`
  } else {
    validation.errorMessage = ''
  }

  return validation
}
